import { Components, Theme } from '@mui/material';
import { label2, label3 } from '../typography/jscss';
import { chipClasses } from './types';
import { Spacing1, Spacing2 } from '../spacing';
import {
  Blue, Brand2, Brand4, Brand5, Content, Focused, Gray, Info,
  Border, Primary, Secondary, Success, Surface, Warning, Error,
} from '../color';

export const MuiChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    color: 'secondary',
  },
  styleOverrides: {
    root: {
      padding: Spacing2,
      '&:hover, &:active': {
        boxShadow: 'none',
      },
      '&:focus-visible': {
        outlineColor: Focused.main,
        outlineStyle: 'solid',
        outlineWidth: 2,
        outlineOffset: 1,
      },
    },
    avatar: {
      marginLeft: 0,
      [`&.${chipClasses.avatarSmall}`]: {
        width: 16,
        height: 16,
      },
      [`&.${chipClasses.avatarMedium}`]: {
        width: 24,
        height: 24,
      },
    },
    labelMedium: {
      ...label2,
    },
    labelSmall: {
      ...label3,
      lineHeight: 'inherit',
    },
    filled: ({ ownerState, theme }) => ({
      [`&.${chipClasses.clickable}`]: {
        '&:hover': {
          // TODO: https://chemaxon.atlassian.net/browse/UXP-564
          /* eslint-disable-next-line */
          // @ts-ignore
          backgroundColor: theme.palette[ownerState.color || 'secondary'].hover,
        },
        '&:active, &:focus-visible': {
          // TODO: https://chemaxon.atlassian.net/browse/UXP-564
          /* eslint-disable-next-line */
          // @ts-ignore
          backgroundColor: theme.palette[ownerState.color || 'secondary'].dark,
          [`&.${chipClasses.colorPrimary}`]: {
            backgroundColor: Primary.default.selected,
          },
          [`&.${chipClasses.colorSecondary}`]: {
            backgroundColor: Secondary.default.selected,
          },
        },
      },
    }),
    subtle: {
      [`.${chipClasses.icon}, .${chipClasses.deleteIcon}`]: {
        color: 'inherit',
        '&:hover': {
          color: 'inherit',
        },
      },
    },
    subtlePrimary: {
      backgroundColor: Primary.container.main,
      color: Primary.container.text,
      ':focus-visible': {
        backgroundColor: Primary.container.main,
      },
      [`&.${chipClasses.clickable}`]: {
        '&:hover': {
          backgroundColor: Primary.container.hover,
        },
        '&:active': {
          // TODO: no direct colors! roles should be used; https://chemaxon.atlassian.net/browse/UXP-567
          border: `2px solid ${Blue[100]}`,
          padding: Spacing1,
          backgroundColor: Primary.container.selected,
        },
      },
    },
    outlinedPrimary: {
      '&:active': {
        borderColor: Primary.default.selected,
      },
    },
    outlinedSecondary: {
      '&:active': {
        borderColor: Secondary.default.active,
      },
    },
    subtleSecondary: {
      backgroundColor: Secondary.container.main,
      color: Secondary.container.text,
      ':focus-visible': {
        backgroundColor: Secondary.container.main,
      },
      [`&.${chipClasses.clickable}`]: {
        '&:hover': {
          backgroundColor: Secondary.container.hover,
        },
        '&:active': {
          // TODO: no direct colors! roles should be used; https://chemaxon.atlassian.net/browse/UXP-567
          border: `2px solid ${Gray[110]}`,
          padding: Spacing1,
          backgroundColor: Secondary.container.selected,
        },
      },
    },
    outlined: {
      padding: Spacing1,
      borderWidth: Spacing1,

      [`&.${chipClasses.colorNeutral}`]: {
        backgroundColor: Surface.dark,
        color: Content.main,
        borderColor: Border.variant.main,
      },
      [`&.${chipClasses.colorWarning}`]: {
        borderColor: Warning.default.main,
        color: Warning.container.text,
        backgroundColor: Warning.container,
      },
      [`&.${chipClasses.colorError}`]: {
        borderColor: Error.default.main,
        color: Error.container.text,
        backgroundColor: Error.container,
      },
      [`&.${chipClasses.colorInfo}`]: {
        borderColor: Info.default.main,
        color: Info.container.text,
        backgroundColor: Info.container,
      },
      [`&.${chipClasses.colorSuccess}`]: {
        borderColor: Success.default.main,
        color: Success.container.text,
        backgroundColor: Success.container,
      },
      [`&.${chipClasses.colorNeutral}`]: {
        borderColor: Border.variant.main,
        color: Content.main,
        backgroundColor: Surface.dark,
      },
      [`&.${chipClasses.colorYellow}`]: {
        borderColor: Brand4.main,
        color: Brand4.text,
        backgroundColor: Brand4.containerMain,
      },
      [`&.${chipClasses.colorTurquoise}`]: {
        borderColor: Brand5.main,
        backgroundColor: Brand5.containerMain,
        color: Brand5.containerText,
      },
      [`&.${chipClasses.colorTurquoiseDark}`]: {
        borderColor: Brand5.containerText,
        backgroundColor: Brand5.containerMain,
        color: Brand5.containerText,
      },
      [`&.${chipClasses.colorFlamingo}`]: {
        borderColor: Brand2.main,
        backgroundColor: Brand2.containerMain,
        color: Brand2.containerText,
      },
    },
  },
};
